// Screen B - the evidence view (docs/design/slice4-certify-ux.md §5.3).
// Route: /apps/:appId/certify/data-room/:documentId (nested inside AppDetail's Outlet -
// `appId` is already validated/tenant-scoped there, same precedent as AppExplained.tsx/
// AskCrew.tsx). NOT BACKED by CERTIFY_ROUTES - a named contract gap (ux spec §5.3,
// §7.1 item 10), not an oversight: this screen calls the PROPOSED
// `getCertificateDetail`/`createShareLink` (src/api/certify.ts), flagged there and in
// the dispatch return.
import { useEffect, useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { isErr } from "@nightcrew/shared";
import type { Certificate } from "@nightcrew/shared";
import type { AppDetailContext } from "../AppDetail.js";
import { createShareLink, getCertificateDetail } from "../../api/certify.js";
import { isOwnerOrAdmin, useSession } from "../../state/session.js";
import { certifyCopy } from "../../copy/certify.js";
import { CredentialCard } from "../../components/CredentialCard.js";
import { DeniedCard } from "../../components/DeniedCard.js";

type DetailState =
  | { readonly status: "loading" }
  | { readonly status: "not_found" }
  | { readonly status: "error" }
  | { readonly status: "ready"; readonly certificate: Certificate };

type ShareState =
  | { readonly status: "idle" }
  | { readonly status: "generating" }
  | { readonly status: "generated"; readonly url: string };

export const EvidenceDetail = (): React.JSX.Element => {
  const { appId, app } = useOutletContext<AppDetailContext>();
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const { state: session } = useSession();
  const canManage = isOwnerOrAdmin(session);
  const [state, setState] = useState<DetailState>({ status: "loading" });
  const [shareState, setShareState] = useState<ShareState>({ status: "idle" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (documentId === undefined) return;
    const cancelled = { current: false };
    setState({ status: "loading" });
    void (async () => {
      const result = await getCertificateDetail(appId, documentId);
      if (cancelled.current) return;
      if (isErr(result)) {
        const notFound = result.error.kind === "boundary" && result.error.error.code === "not_found";
        setState(notFound ? { status: "not_found" } : { status: "error" });
        return;
      }
      setState({ status: "ready", certificate: result.value });
    })();
    return () => {
      cancelled.current = true;
    };
  }, [appId, documentId, attempt]);

  if (documentId === undefined || state.status === "not_found") {
    return (
      <div className="wrap">
        <DeniedCard
          title={certifyCopy("certify.dataRoom.evidence.notFound")}
          message="It may not exist, or it isn't on your account."
        />
      </div>
    );
  }

  if (state.status === "loading") {
    return (
      <div className="wrap">
        <div className="card skeleton-card" />
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div className="wrap">
        <div className="card state-card">
          <p>We could not load this document. Try again.</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setAttempt((a) => a + 1);
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const handleShareLink = (): void => {
    setShareState({ status: "generating" });
    void (async () => {
      const result = await createShareLink(appId, documentId);
      setShareState(isErr(result) ? { status: "idle" } : { status: "generated", url: result.value.url });
    })();
  };

  return (
    <div className="wrap">
      <button
        type="button"
        className="btn btn-ghost btn-sm"
        onClick={() => {
          void navigate(-1);
        }}
      >
        Back
      </button>
      <div className="mt16">
        <CredentialCard certificate={state.certificate} appName={app.name} />
      </div>
      {canManage ? (
        <div className="card pad mt16">
          {shareState.status === "generated" ? (
            <div className="key-card" data-testid="share-link-shown-once">
              <p className="muted" style={{ fontSize: 12.5 }}>
                {certifyCopy("certify.dataRoom.evidence.shareLinkNote")}
              </p>
              <div className="key-value field-mono" style={{ userSelect: "all" }}>
                {shareState.url}
              </div>
              <button
                type="button"
                className="btn btn-ghost btn-sm mt8"
                onClick={() => {
                  navigator.clipboard.writeText(shareState.url).catch(() => undefined);
                }}
              >
                {certifyCopy("certify.dataRoom.evidence.shareLinkGeneratedCta")}
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="btn btn-ghost"
              disabled={shareState.status === "generating"}
              onClick={handleShareLink}
            >
              {certifyCopy("certify.dataRoom.evidence.shareLinkCta")}
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
};
